/**
 * Supporting-file upload modal
 *
 * The write half of the review page's Supporting Files tab: a batch uploader
 * ported from the old standalone upload page. Unlike that page,
 * deal and period are not inputs here — they are fixed by the submission the
 * review page is showing, and appear only as read-only context text.
 *
 * The modal owns batching and concurrency so the shell only has to supply a
 * per-file `onUpload`.
 */

import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button, DialogFooter, Loader, Modal } from "@/ui/atoms";
import { FileUploadZone } from "@/ui/organisms";
import { FormField } from "@/ui/molecules";

type TFileUploadStatus = "pending" | "uploading" | "success" | "error";

/**
 * Merge updated rows into the current list by File identity, leaving the rest as they
 * are. A batch only ever covers the rows being sent that time, so every write back has
 * to be a merge — an assignment would discard the others.
 */
function mergeRowsByFile(prev: TFileRow[], updated: TFileRow[]): TFileRow[] {
    return prev.map((row) => updated.find((u) => u.file === row.file) ?? row);
}

type TFileRow = {
    file: File;
    friendlyName: string;
    financialFileId: string;
    notes: string;
    status: TFileUploadStatus;
    error?: string;
};

export type TSupportingFileUploadModalProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Fixed scope, shown as read-only context rather than as inputs. */
    dealId: string;
    dealName: string | null;
    period: string;
    /** Financial files in this deal a supporting file may be linked to. */
    relatableFiles: { id: string; name: string }[];
    onUpload: (input: {
        file: File;
        friendlyName: string;
        financialFileId: string | null;
        notes: string | null;
    }) => Promise<void>;
    /** Called once after a batch in which at least one file succeeded. */
    onUploaded: () => void;
};

/** Accepted file types for supporting files */
const supportingFileAccept: Record<string, string[]> = {
    "application/pdf": [".pdf"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.ms-powerpoint": [".ppt"],
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/gif": [".gif"],
    "image/bmp": [".bmp"],
    "image/tiff": [".tiff"],
    "text/plain": [".txt"],
    "text/csv": [".csv"],
};

const MAX_FILES = 25;
const CONCURRENCY_LIMIT = 3;

function stripExtension(filename: string): string {
    const lastDot = filename.lastIndexOf(".");
    return lastDot > 0 ? filename.substring(0, lastDot) : filename;
}

const PERIOD_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
});

function formatPeriod(period: string): string {
    return PERIOD_FORMATTER.format(new Date(period));
}

export function SupportingFileUploadModal(
    props: TSupportingFileUploadModalProps,
): React.ReactElement {
    const { open, onOpenChange, dealId, dealName, period, relatableFiles, onUpload, onUploaded } =
        props;

    const [rows, setRows] = useState<TFileRow[]>([]);
    const [selectionError, setSelectionError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Forces FileUploadZone to remount on every open, so its own internal
    // selection state can never leak from one batch into the next.
    const [sessionId, setSessionId] = useState(0);

    // Re-seed whenever the modal (re)opens, so a previous batch never carries over.
    useEffect(() => {
        if (open) {
            setRows([]);
            setSelectionError(null);
            setIsSubmitting(false);
            setSessionId((id) => id + 1);
        }
    }, [open]);

    // FileUploadZone (with hideFileList) reports only the files accepted in
    // each specific drop/pick, not an accumulated total — this modal is the
    // single source of truth for the full selection, so new files are simply
    // appended to the existing rows. Rows already on screen are never rebuilt,
    // so their edits can't be discarded by a later selection, and removal
    // (below) can't be undone by one either — see FIN-611 review history:
    // an earlier version rebuilt the whole list from an accumulated total on
    // every call, which both discarded in-progress edits and, once removal
    // was added, resurrected files the user had just removed.
    const handleFilesSelect = useCallback(
        (newFiles: File[]) => {
            if (rows.length + newFiles.length > MAX_FILES) {
                setSelectionError(`You can upload at most ${MAX_FILES} files at once.`);
                return;
            }
            setSelectionError(null);
            const additions: TFileRow[] = newFiles.map((file) => ({
                file,
                friendlyName: stripExtension(file.name),
                financialFileId: "",
                notes: "",
                status: "pending" as const,
            }));
            setRows((prev) => [...prev, ...additions]);
        },
        [rows.length],
    );

    const updateRow = useCallback((index: number, patch: Partial<TFileRow>) => {
        setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    }, []);

    const removeRow = useCallback((index: number) => {
        setRows((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const uploadWithConcurrency = useCallback(
        async (entries: TFileRow[]): Promise<TFileRow[]> => {
            const queue = [...entries];
            const results: TFileRow[] = [...entries];

            const worker = async () => {
                while (queue.length > 0) {
                    const entry = queue.shift();
                    if (!entry) break;
                    const idx = results.indexOf(entry);

                    results[idx] = { ...entry, status: "uploading" };
                    // Merged rather than assigned: `entries` is only the rows being
                    // sent this time, so replacing the whole list would drop rows that
                    // succeeded in an earlier batch off the screen mid-upload.
                    setRows((prev) => mergeRowsByFile(prev, results));

                    try {
                        await onUpload({
                            file: entry.file,
                            friendlyName: entry.friendlyName,
                            financialFileId: entry.financialFileId || null,
                            notes: entry.notes.trim() || null,
                        });
                        results[idx] = { ...entry, status: "success" };
                    } catch (err: unknown) {
                        const message = err instanceof Error ? err.message : "Upload failed";
                        results[idx] = { ...entry, status: "error", error: message };
                    }
                    setRows((prev) => mergeRowsByFile(prev, results));
                }
            };

            const workers = Array.from(
                { length: Math.min(CONCURRENCY_LIMIT, entries.length) },
                () => worker(),
            );
            await Promise.all(workers);
            return results;
        },
        [onUpload],
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;

        // Only rows that have not already landed are sent. The modal deliberately
        // stays open after a batch — `onUploaded` refreshes the list behind it rather
        // than closing it — so submitting again is the natural way to retry the
        // failures. Re-sending the whole list would create a second copy of every
        // file that succeeded the first time.
        const retryable = rows.filter((row) => row.status !== "success");
        if (retryable.length === 0) return;

        setIsSubmitting(true);
        const queued = retryable.map((row) => ({
            ...row,
            status: "pending" as const,
            error: undefined,
        }));
        setRows((prev) => mergeRowsByFile(prev, queued));
        const results = await uploadWithConcurrency(queued);
        setRows((prev) => mergeRowsByFile(prev, results));
        setIsSubmitting(false);

        if (results.some((row) => row.status === "success")) {
            onUploaded();
        }
    };

    return (
        <Modal
            open={open}
            onOpenChange={onOpenChange}
            title="Upload Supporting Files"
            className="max-h-[85vh] overflow-y-auto"
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <p data-testid="supporting-upload-scope" className="text-sm text-muted-foreground">
                    {(dealName ?? dealId) + " — " + formatPeriod(period)}
                </p>

                <FormField label={`Files (up to ${MAX_FILES})`} htmlFor="supportingFile" required>
                    <FileUploadZone
                        key={sessionId}
                        id="supportingFile"
                        multiple
                        maxFiles={MAX_FILES}
                        hideFileList
                        onFilesSelect={handleFilesSelect}
                        accept={supportingFileAccept}
                        maxSize={50 * 1024 * 1024}
                        disabled={isSubmitting}
                        className="min-h-[120px]"
                    />
                </FormField>

                {selectionError && (
                    <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                        {selectionError}
                    </div>
                )}

                {rows.map((row, index) => (
                    <div
                        key={`${row.file.name}-${index}`}
                        className="rounded-lg border p-3 space-y-3"
                    >
                        <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium truncate">{row.file.name}</p>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                data-testid={`supporting-upload-remove-${index}`}
                                aria-label={`Remove ${row.file.name}`}
                                onClick={() => removeRow(index)}
                                disabled={isSubmitting}
                                className="shrink-0"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        <FormField label="Name" htmlFor={`supporting-upload-name-${index}`}>
                            <input
                                id={`supporting-upload-name-${index}`}
                                data-testid={`supporting-upload-name-${index}`}
                                type="text"
                                value={row.friendlyName}
                                onChange={(e) => updateRow(index, { friendlyName: e.target.value })}
                                disabled={isSubmitting}
                                maxLength={255}
                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        </FormField>

                        <FormField
                            label="Related file"
                            htmlFor={`supporting-upload-related-${index}`}
                            description="Optionally link to a specific financial file"
                        >
                            <select
                                id={`supporting-upload-related-${index}`}
                                data-testid={`supporting-upload-related-${index}`}
                                value={row.financialFileId}
                                onChange={(e) =>
                                    updateRow(index, { financialFileId: e.target.value })
                                }
                                disabled={isSubmitting}
                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                                <option value="">None (optional)</option>
                                {relatableFiles.map((file) => (
                                    <option key={file.id} value={file.id}>
                                        {file.name}
                                    </option>
                                ))}
                            </select>
                        </FormField>

                        <FormField label="Notes" htmlFor={`supporting-upload-notes-${index}`}>
                            <textarea
                                id={`supporting-upload-notes-${index}`}
                                data-testid={`supporting-upload-notes-${index}`}
                                value={row.notes}
                                onChange={(e) => updateRow(index, { notes: e.target.value })}
                                disabled={isSubmitting}
                                maxLength={500}
                                rows={2}
                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                            />
                        </FormField>

                        {row.status === "uploading" && (
                            <p className="text-xs text-muted-foreground">Uploading…</p>
                        )}
                        {row.status === "success" && (
                            <p className="text-xs text-green-600">Uploaded</p>
                        )}
                        {row.status === "error" && row.error && (
                            <p className="text-sm text-destructive">{row.error}</p>
                        )}
                    </div>
                ))}

                <DialogFooter className="gap-2 pt-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        data-testid="supporting-upload-submit"
                        disabled={
                            rows.length === 0 ||
                            isSubmitting ||
                            // Everything already landed — there is nothing left to send,
                            // and the modal stays open, so leaving this live invites a
                            // second click that would duplicate the whole batch.
                            rows.every((row) => row.status === "success")
                        }
                    >
                        {isSubmitting ? (
                            <>
                                <Loader size="sm" className="mr-2" />
                                Uploading...
                            </>
                        ) : (
                            <>
                                Upload {rows.length || ""} File
                                {rows.length !== 1 ? "s" : ""}
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </form>
        </Modal>
    );
}
